import { Button } from '@tremor/react';
import { PAGE_SIZES } from '../lib/usePagination';
import { formatInt } from '../lib/format';
import { t } from '../lib/i18n';

/**
 * Pager strip for a paginated table. Feed it the state from usePagination
 * (`<Pagination {...pg} />`); it renders nothing when everything fits on one
 * page so short lists look exactly as they did before. Carries `no-print`
 * because the hook expands to every row while printing.
 */
export interface PaginationProps {
  page: number;
  pageCount: number;
  pageSize: number;
  total: number;
  from: number;
  to: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  className?: string;
}

export default function Pagination({
  page, pageCount, pageSize, total, from, to, setPage, setPageSize, className,
}: PaginationProps) {
  if (pageCount <= 1) return null;
  return (
    <div
      className={
        'no-print flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-tremor-content px-1 py-2' +
        (className ? ' ' + className : '')
      }
    >
      <span className="tabular-nums">
        {t.pagination.showing} {formatInt(from)}–{formatInt(to)} {t.pagination.of} {formatInt(total)}
      </span>
      <span className="flex items-center gap-2 sm:ml-auto">
        <span>{t.pagination.perPage}</span>
        <select
          aria-label={t.pagination.perPage}
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          className="rounded-tremor-small border border-tremor-border bg-tremor-background px-2 py-1 text-sm text-tremor-content-strong"
        >
          {PAGE_SIZES.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
          {/* Keep a non-preset size selectable so the control never shows a blank. */}
          {!(PAGE_SIZES as readonly number[]).includes(pageSize) && (
            <option value={pageSize}>{pageSize}</option>
          )}
        </select>
      </span>
      <span className="flex items-center gap-2">
        <Button size="xs" variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
          ‹ {t.pagination.prev}
        </Button>
        <span className="tabular-nums whitespace-nowrap">
          {t.pagination.page} {formatInt(page)} {t.pagination.of} {formatInt(pageCount)}
        </span>
        <Button size="xs" variant="secondary" disabled={page >= pageCount} onClick={() => setPage(page + 1)}>
          {t.pagination.next} ›
        </Button>
      </span>
    </div>
  );
}
